import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { 
  AlertTriangle, 
  ArrowUpRight,
  MapPin,
  AlertCircle,
  CheckCircle,
  Clock,
  Calendar,
  Settings2,
  Eye,
  EyeOff,
  ExternalLink,
  Bookmark,
  ListFilter,
  Search,
  Building2,
  Users,
  Ruler,
  Home,
  FileText,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  X,
  Layers,
  Download,
  Shield,
  Loader2
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { Link, useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

// Dataset display names
const DATASET_LABELS = {
  dob_violations: 'DOB Violations',
  fire_violations: 'Fire Violations',
  ecb_violations: 'ECB Violations',
  permits: 'DOB BIS Applications',
  mta: 'MTA',
  dot_violations: 'DOT Violations',
  dob_now: 'DOB NOW Build',
  bsa_applications: 'BSA Applications',
  hpd_violations: 'HPD Violations',
  oath_hearings: 'OATH Hearings',
  dep_environmental: 'DEP Environmental',
  certificate_of_occupancy: 'Certificate of Occupancy',
  oer_sites: 'OER Cleanup Sites',
  e_designations: 'E-Designations'
};

// Date range options
const DATE_RANGES = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: '180', label: 'Last 6 Months' },
  { value: '365', label: 'Last Year' },
  { value: 'all', label: 'All Time' },
];

export default function DashboardPage() {
  const { user, api } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [propertyStats, setPropertyStats] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [dateRange, setDateRange] = useState('all'); // Default to all time
  
  // Watchlist selection state
  const [availableWatchlists, setAvailableWatchlists] = useState([]);
  const [selectedWatchlists, setSelectedWatchlists] = useState([]); // Empty = all watchlists
  
  // Property detail modal state
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyDetailOpen, setPropertyDetailOpen] = useState(false);
  const [loadingPropertyDetail, setLoadingPropertyDetail] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [propertyRiskScore, setPropertyRiskScore] = useState(null);
  const [loadingRiskScore, setLoadingRiskScore] = useState(false);
  
  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    address: true,
    blockLot: true,
    watchlist: true,
    open: true,
    inProgress: true,
    closed: true,
    highSeverity: true,
    total: true
  });

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });

  const toggleColumn = (column) => {
    setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
  };

  // Handle column sort
  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort the property stats
  const sortedPropertyStats = [...propertyStats].sort((a, b) => {
    if (!sortConfig.column) return 0;
    
    let aVal, bVal;
    switch (sortConfig.column) {
      case 'address':
        aVal = a.address || '';
        bVal = b.address || '';
        break;
      case 'blockLot':
        aVal = `${a.block}-${a.lot}`;
        bVal = `${b.block}-${b.lot}`;
        break;
      case 'watchlist':
        aVal = a.watchlist_name || '';
        bVal = b.watchlist_name || '';
        break;
      case 'open':
        aVal = a.open || 0;
        bVal = b.open || 0;
        break;
      case 'inProgress':
        aVal = a.in_progress || 0;
        bVal = b.in_progress || 0;
        break;
      case 'closed':
        aVal = a.closed || 0;
        bVal = b.closed || 0;
        break;
      case 'highSeverity':
        aVal = a.high_severity || 0;
        bVal = b.high_severity || 0;
        break;
      case 'total':
        aVal = a.total || 0;
        bVal = b.total || 0;
        break;
      default:
        return 0;
    }
    
    if (typeof aVal === 'string') {
      return sortConfig.direction === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    }
    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
  });

  // Sort indicator component
  const SortIndicator = ({ column }) => {
    if (sortConfig.column !== column) {
      return <ChevronUp className="w-3 h-3 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-3 h-3 text-cyan-400" />
      : <ChevronDown className="w-3 h-3 text-cyan-400" />;
  };
  
  // Borough code mapping for DOB links
  const getBoroughCode = (borough) => {
    const codes = {
      'Manhattan': '1',
      'Bronx': '2', 
      'Brooklyn': '3',
      'Queens': '4',
      'Staten Island': '5'
    };
    return codes[borough] || '1';
  };
  
  // Get DOB BIS Web URL
  const getDOBProfileUrl = (prop) => {
    const boroCode = getBoroughCode(prop.borough || 'Manhattan');
    return `https://a810-bisweb.nyc.gov/bisweb/PropertyProfileOverviewServlet?boro=${boroCode}&block=${prop.block}&lot=${prop.lot}`;
  };
  
  // Get Google Street View URL
  const getStreetViewUrl = (address, borough = 'Manhattan') => {
    const fullAddress = `${address}, ${borough}, New York, NY`;
    return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=40.7128,-74.0060&query=${encodeURIComponent(fullAddress)}`;
  };
  
  // Get Street View Image URL (static image)
  const getStreetViewImageUrl = (address, borough = 'Manhattan') => {
    const fullAddress = `${address}, ${borough}, New York, NY`;
    // Using a placeholder service - in production use Google Street View Static API with key
    return `https://maps.googleapis.com/maps/api/streetview?size=600x300&location=${encodeURIComponent(fullAddress)}&key=YOUR_API_KEY`;
  };
  
  // Generate compliance report PDF for selected property
  // Generate Enhanced Report (2-tier: Executive or Full)
  const generateEnhancedReport = async (prop, reportType = 'executive') => {
    if (!prop?.address) {
      toast.error('Unable to generate report - Address not found');
      return;
    }
    
    setGeneratingReport(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        address: prop.address,
        borough: prop.borough || 'Manhattan',
        report_type: reportType
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/enhanced-report-by-address?${params}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeAddress = (prop.address || 'property').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const label = reportType === 'extended' ? 'Extended' : 'Executive';
      link.download = `Compliance_${label}_${safeAddress}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${label} report downloaded!`);
    } catch (err) {
      console.error('Failed to generate report:', err);
      toast.error(err.message || 'Failed to generate report. Please try again.');
    } finally {
      setGeneratingReport(false);
    }
  };
  
  // Open property detail modal
  const openPropertyDetail = async (prop) => {
    setSelectedProperty(prop);
    setPropertyDetailOpen(true);
    setLoadingPropertyDetail(true);
    setPropertyRiskScore(null);
    setLoadingRiskScore(true);
    
    // Fetch risk score
    try {
      const riskRes = await api().post('/property/risk-score', {
        block: prop.block,
        lot: prop.lot,
        borough: prop.borough,
        address: prop.address,
        include_ai_analysis: true
      });
      setPropertyRiskScore(riskRes.data);
    } catch (err) {
      console.error('Failed to fetch risk score:', err);
      setPropertyRiskScore(null);
    } finally {
      setLoadingRiskScore(false);
    }
    
    // Fetch live property info from NYC PLUTO API
    try {
      const borough = prop.borough || 'Manhattan';
      const block = prop.block || '';
      const lot = prop.lot || '';
      
      if (!block || !lot) {
        throw new Error('Missing block or lot information');
      }
      
      const res = await api().get(`/property/pluto?borough=${encodeURIComponent(borough)}&block=${block}&lot=${lot}`);
      
      if (res.data.success && res.data.data) {
        setSelectedProperty(prev => ({ ...prev, plutoData: res.data.data }));
      } else {
        // If PLUTO API fails, show error state
        console.warn('PLUTO API returned no data:', res.data.error);
        setSelectedProperty(prev => ({ ...prev, plutoData: null, plutoError: res.data.error || 'Property not found in PLUTO database' }));
      }
    } catch (err) {
      console.error('Failed to fetch property details:', err);
      setSelectedProperty(prev => ({ ...prev, plutoData: null, plutoError: 'Unable to fetch property details' }));
    } finally {
      setLoadingPropertyDetail(false);
    }
  };
  
  const toggleWatchlist = (watchlistId) => {
    setSelectedWatchlists(prev => {
      if (prev.includes(watchlistId)) {
        return prev.filter(id => id !== watchlistId);
      } else {
        return [...prev, watchlistId];
      }
    });
  };
  
  const selectAllWatchlists = () => {
    setSelectedWatchlists([]);
  };

  const columnConfig = [
    { key: 'address', label: 'Property Address' },
    { key: 'blockLot', label: 'Block/Lot' },
    { key: 'watchlist', label: 'Watchlist' },
    { key: 'open', label: 'Open' },
    { key: 'inProgress', label: 'In Progress' },
    { key: 'closed', label: 'Closed' },
    { key: 'highSeverity', label: 'High Severity' },
    { key: 'total', label: 'Total' },
  ];

  // Check if a date is within the selected range
  const isWithinDateRange = useCallback((dateStr) => {
    if (dateRange === 'all') return true;
    
    const issueDate = new Date(dateStr);
    const now = new Date();
    const daysAgo = parseInt(dateRange);
    const cutoffDate = new Date(now.setDate(now.getDate() - daysAgo));
    
    return issueDate >= cutoffDate;
  }, [dateRange]);

  // Fetch violation stats for a single property
  const fetchPropertyViolations = useCallback(async (property) => {
    const datasets = user?.datasets_access || ['dob_violations'];
    let open = 0, inProgress = 0, closed = 0, high = 0;
    
    for (const dataset of datasets) {
      try {
        const params = new URLSearchParams({ dataset, limit: '100' });
        if (property.address) params.append('address', property.address);
        
        const res = await api().get(`/violations/search?${params}`);
        const violations = res.data.data || [];
        
        // Filter by date range
        const filteredViolations = violations.filter(v => isWithinDateRange(v.issue_date));
        
        filteredViolations.forEach(v => {
          if (v.status === 'Open') open++;
          else if (v.status === 'In Progress') inProgress++;
          else if (v.status === 'Closed') closed++;
          if (v.severity === 'High') high++;
        });
      } catch (err) {
        console.error('Error fetching violations:', err);
      }
    }
    
    return { 
      address: property.address, 
      block: property.block, 
      lot: property.lot,
      open, 
      inProgress, 
      closed, 
      total: open + inProgress + closed,
      high 
    };
  }, [api, user?.datasets_access, isWithinDateRange]);

  // Fetch dashboard stats using fast cached endpoint
  const fetchPropertyStats = useCallback(async () => {
    setLoadingProperties(true);
    try {
      // Build query with date filter and watchlist filter
      const params = new URLSearchParams();
      if (dateRange !== 'all') {
        params.append('days', dateRange);
      }
      if (selectedWatchlists.length > 0) {
        params.append('watchlist_ids', selectedWatchlists.join(','));
      }
      
      const url = `/dashboard/quick-stats${params.toString() ? '?' + params.toString() : ''}`;
      const res = await api().get(url);
      const quickStats = res.data;
      
      // Update available watchlists
      if (quickStats.available_watchlists) {
        setAvailableWatchlists(quickStats.available_watchlists);
      }
      
      // Use property stats from sync results if available
      if (quickStats.property_stats && quickStats.property_stats.length > 0) {
        setPropertyStats(quickStats.property_stats);
      } else if (quickStats.properties && quickStats.properties.length > 0) {
        // Fallback to property list with watchlist info
        const basicStats = quickStats.properties.map(p => ({
          address: p.address,
          block: p.block,
          lot: p.lot,
          borough: p.borough || 'Manhattan',
          watchlist: p.watchlist || '',
          open: 0,
          inProgress: 0,
          closed: 0,
          total: 0,
          high: 0
        }));
        setPropertyStats(basicStats);
      } else {
        setPropertyStats([]);
      }
    } catch (err) {
      console.error('Failed to fetch property stats:', err);
    } finally {
      setLoadingProperties(false);
    }
  }, [api, dateRange, selectedWatchlists]);

  // Re-fetch when date range changes
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await api().get('/violations/stats');
        setStats(res.data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [api]);

  // Fetch property stats on mount and when date range changes
  useEffect(() => {
    fetchPropertyStats();
  }, [fetchPropertyStats, dateRange]);

  // Calculate totals from property stats (with safe defaults)
  const totals = propertyStats.reduce((acc, p) => ({
    open: acc.open + (p.open || 0),
    inProgress: acc.inProgress + (p.inProgress || 0),
    closed: acc.closed + (p.closed || 0),
    high: acc.high + (p.high || 0)
  }), { open: 0, inProgress: 0, closed: 0, high: 0 });

  const navigate = useNavigate();

  const statCards = [
    { 
      label: 'Open Violations', 
      value: totals.open, 
      icon: AlertTriangle, 
      color: 'text-rose-400',
      bg: 'bg-rose-500/20',
      link: '/compliance?status=open',
      filterType: 'open'
    },
    { 
      label: 'In Progress', 
      value: totals.inProgress, 
      icon: Clock, 
      color: 'text-amber-400',
      bg: 'bg-amber-500/20',
      link: '/compliance?status=in_progress',
      filterType: 'in_progress'
    },
    { 
      label: 'Closed', 
      value: totals.closed, 
      icon: CheckCircle, 
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/20',
      link: '/compliance?status=closed',
      filterType: 'closed'
    },
    { 
      label: 'High Severity', 
      value: totals.high, 
      icon: AlertCircle, 
      color: 'text-rose-400',
      bg: 'bg-rose-500/20',
      link: '/compliance?severity=high',
      filterType: 'high'
    },
  ];

  const severityData = stats?.by_severity ? [
    { name: 'High', value: stats.by_severity.high, fill: '#f43f5e' },
    { name: 'Medium', value: stats.by_severity.medium, fill: '#f59e0b' },
    { name: 'Low', value: stats.by_severity.low, fill: '#3b82f6' },
  ] : [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header with Date Range Selector */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Dashboard</h1>
            <p className="text-zinc-400">Welcome back, {user?.name}</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-2 bg-zinc-900/80 px-3 py-2 rounded-lg border border-white/10">
              <Calendar className="w-4 h-4 text-indigo-400" />
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[140px] bg-transparent border-0 text-white text-sm h-auto p-0 focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_RANGES.map(range => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="badge-info capitalize">{user?.tier}</span>
          </div>
        </div>

        {/* Stat Cards - Clickable */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {statCards.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div 
                key={i} 
                className="glass-card p-4 rounded-xl cursor-pointer hover:bg-white/10 transition-all group"
                data-testid={`stat-card-${i}`}
                onClick={() => navigate(stat.link)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${stat.color}`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xl font-bold text-white">{loadingProperties ? '-' : stat.value}</p>
                      <p className="text-xs text-zinc-400 truncate">{stat.label}</p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Property Stats Table */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Watchlist Logo/Icon */}
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Bookmark className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Properties Overview</h3>
                <p className="text-xs text-zinc-500">
                  <span className="text-indigo-400">Watchlisted properties only</span>
                  {' • '}{DATE_RANGES.find(r => r.value === dateRange)?.label}
                  {' • '}{propertyStats.length} {propertyStats.length === 1 ? 'property' : 'properties'}
                  {selectedWatchlists.length > 0 && ` • ${selectedWatchlists.length} watchlist${selectedWatchlists.length > 1 ? 's' : ''} filtered`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Watchlist Selector Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className={`${selectedWatchlists.length > 0 ? 'text-indigo-400' : 'text-zinc-400'} hover:text-white`}>
                    <ListFilter className="w-4 h-4 mr-1" />
                    Watchlists
                    {selectedWatchlists.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-indigo-500/30 rounded">{selectedWatchlists.length}</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-zinc-900 border-white/10">
                  <DropdownMenuLabel className="text-zinc-400 text-xs flex items-center gap-2">
                    <Bookmark className="w-3 h-3" />
                    Select Watchlists
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuCheckboxItem
                    checked={selectedWatchlists.length === 0}
                    onCheckedChange={selectAllWatchlists}
                    className="text-zinc-300 focus:bg-white/10 focus:text-white"
                  >
                    <span className="flex items-center gap-2">
                      {selectedWatchlists.length === 0 && <CheckCircle className="w-3 h-3 text-emerald-400" />}
                      All Watchlists
                    </span>
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {availableWatchlists.map(wl => (
                    <DropdownMenuCheckboxItem
                      key={wl.id}
                      checked={selectedWatchlists.includes(wl.id)}
                      onCheckedChange={() => toggleWatchlist(wl.id)}
                      className="text-zinc-300 focus:bg-white/10 focus:text-white"
                    >
                      <span className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
                        {wl.name}
                      </span>
                    </DropdownMenuCheckboxItem>
                  ))}
                  {availableWatchlists.length === 0 && (
                    <div className="px-2 py-3 text-xs text-zinc-500 text-center">
                      No watchlists created yet
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Column Visibility Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                    <Settings2 className="w-4 h-4 mr-1" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-white/10">
                  <DropdownMenuLabel className="text-zinc-400 text-xs">Toggle Columns</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  {columnConfig.map(col => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleColumn(col.key)}
                      className="text-zinc-300 focus:bg-white/10 focus:text-white"
                    >
                      {visibleColumns[col.key] ? (
                        <Eye className="w-3 h-3 mr-2 text-emerald-400" />
                      ) : (
                        <EyeOff className="w-3 h-3 mr-2 text-zinc-500" />
                      )}
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Link to="/watchlist" className="text-sm text-indigo-400 hover:text-indigo-300">
                Manage Watchlist →
              </Link>
            </div>
          </div>
          
          {loadingProperties ? (
            <div className="p-8 text-center">
              <div className="spinner mx-auto" />
            </div>
          ) : propertyStats.length === 0 ? (
            <div className="p-8">
              {/* Onboarding Checklist Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-500/20 to-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Layers className="w-8 h-8 text-indigo-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Let's Get Your Dashboard Ready</h4>
                <p className="text-zinc-400 text-sm max-w-md mx-auto">
                  Complete these steps to start monitoring building compliance in real-time
                </p>
              </div>
              
              {/* Onboarding Steps */}
              <div className="max-w-lg mx-auto space-y-3">
                {/* Step 1 - Active */}
                <Link 
                  to="/compliance" 
                  className="flex items-center gap-4 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/30 hover:border-indigo-500/50 transition-all group"
                  data-testid="onboarding-step-1"
                >
                  <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <h5 className="text-white font-medium group-hover:text-indigo-300 transition-colors">Track your first property</h5>
                    <p className="text-xs text-zinc-400">Search and add a property to your watchlist</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-indigo-400 group-hover:translate-x-1 transition-transform" />
                </Link>
                
                {/* Step 2 - Locked */}
                <div 
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 opacity-50"
                  data-testid="onboarding-step-2"
                >
                  <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-zinc-400 font-bold flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <h5 className="text-zinc-400 font-medium">Configure email alerts</h5>
                    <p className="text-xs text-zinc-500">Get notified when new violations appear</p>
                  </div>
                  <span className="text-xs text-zinc-600 bg-zinc-800 px-2 py-1 rounded">Locked</span>
                </div>
                
                {/* Step 3 - Locked */}
                <div 
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 opacity-50"
                  data-testid="onboarding-step-3"
                >
                  <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center text-zinc-400 font-bold flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <h5 className="text-zinc-400 font-medium">Download a sample report</h5>
                    <p className="text-xs text-zinc-500">See what a compliance report looks like</p>
                  </div>
                  <span className="text-xs text-zinc-600 bg-zinc-800 px-2 py-1 rounded">Locked</span>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="mt-8 pt-6 border-t border-white/10 text-center">
                <p className="text-xs text-zinc-500 mb-3">Or jump directly to:</p>
                <div className="flex items-center justify-center gap-3">
                  <Link to="/watchlist" className="btn-ghost text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                    <Bookmark className="w-3 h-3" />
                    Manage Watchlists
                  </Link>
                  <Link to="/chat" className="btn-ghost text-xs px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                    <FileText className="w-3 h-3" />
                    Help
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-zinc-500 text-xs uppercase bg-white/5">
                    {visibleColumns.address && (
                      <th className="px-4 py-3 cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('address')}>
                        <span className="flex items-center gap-1">
                          Property Address <SortIndicator column="address" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.blockLot && (
                      <th className="px-4 py-3 text-center cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('blockLot')}>
                        <span className="flex items-center justify-center gap-1">
                          Block/Lot <SortIndicator column="blockLot" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.watchlist && (
                      <th className="px-4 py-3 cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('watchlist')}>
                        <span className="flex items-center gap-1">
                          <Bookmark className="w-3 h-3 text-indigo-400" /> Watchlist <SortIndicator column="watchlist" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.open && (
                      <th className="px-4 py-3 text-center cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('open')}>
                        <span className="flex items-center justify-center gap-1">
                          <AlertCircle className="w-3 h-3 text-rose-400" /> Open <SortIndicator column="open" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.inProgress && (
                      <th className="px-4 py-3 text-center cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('inProgress')}>
                        <span className="flex items-center justify-center gap-1">
                          <Clock className="w-3 h-3 text-amber-400" /> In Progress <SortIndicator column="inProgress" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.closed && (
                      <th className="px-4 py-3 text-center cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('closed')}>
                        <span className="flex items-center justify-center gap-1">
                          <CheckCircle className="w-3 h-3 text-emerald-400" /> Closed <SortIndicator column="closed" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.highSeverity && (
                      <th className="px-4 py-3 text-center cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('highSeverity')}>
                        <span className="flex items-center justify-center gap-1">
                          High Severity <SortIndicator column="highSeverity" />
                        </span>
                      </th>
                    )}
                    {visibleColumns.total && (
                      <th className="px-4 py-3 text-center cursor-pointer hover:text-zinc-300 transition-colors" onClick={() => handleSort('total')}>
                        <span className="flex items-center justify-center gap-1">
                          Total <SortIndicator column="total" />
                        </span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {sortedPropertyStats.map((prop, i) => (
                    <tr key={i} className="hover:bg-white/5 transition-colors cursor-pointer" onClick={() => openPropertyDetail(prop)}>
                      {visibleColumns.address && (
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                            <span className="text-white font-medium">{prop.address}</span>
                          </div>
                        </td>
                      )}
                      {visibleColumns.blockLot && (
                        <td className="px-4 py-3 text-center text-zinc-400">
                          {prop.block}/{prop.lot}
                        </td>
                      )}
                      {visibleColumns.watchlist && (
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 text-xs bg-indigo-500/20 text-indigo-400 px-2 py-1 rounded">
                            <Bookmark className="w-3 h-3" />
                            {prop.watchlist || 'Default'}
                          </span>
                        </td>
                      )}
                      {visibleColumns.open && (
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded ${
                            prop.open > 0 ? 'bg-rose-500/20 text-rose-400' : 'bg-zinc-800 text-zinc-500'
                          }`}>
                            {prop.open}
                          </span>
                        </td>
                      )}
                      {visibleColumns.inProgress && (
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded ${
                            prop.inProgress > 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-500'
                          }`}>
                            {prop.inProgress}
                          </span>
                        </td>
                      )}
                      {visibleColumns.closed && (
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded ${
                            prop.closed > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-800 text-zinc-500'
                          }`}>
                            {prop.closed}
                          </span>
                        </td>
                      )}
                      {visibleColumns.highSeverity && (
                        <td className="px-4 py-3 text-center">
                          {prop.high > 0 ? (
                            <span className="inline-flex items-center gap-1 text-rose-400">
                              <AlertTriangle className="w-3 h-3" /> {prop.high}
                            </span>
                          ) : (
                            <span className="text-zinc-500">0</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.total && (
                        <td className="px-4 py-3 text-center text-white font-medium">
                          {prop.total}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Property Detail Modal */}
        <Dialog open={propertyDetailOpen} onOpenChange={setPropertyDetailOpen}>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-white">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-lg">{selectedProperty?.address}</div>
                  <div className="text-sm text-zinc-500 font-normal">
                    Block {selectedProperty?.block} • Lot {selectedProperty?.lot} • {selectedProperty?.borough || 'Manhattan'}
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>
            
            {loadingPropertyDetail ? (
              <div className="py-12 text-center">
                <div className="spinner mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">Loading property details...</p>
              </div>
            ) : selectedProperty?.plutoData ? (
              <div className="space-y-6 py-4">
                {/* Risk Score Card */}
                <div className={`p-4 rounded-xl border ${
                  propertyRiskScore?.score >= 70 ? 'bg-rose-500/10 border-rose-500/30' :
                  propertyRiskScore?.score >= 50 ? 'bg-orange-500/10 border-orange-500/30' :
                  propertyRiskScore?.score >= 30 ? 'bg-amber-500/10 border-amber-500/30' :
                  'bg-emerald-500/10 border-emerald-500/30'
                }`}>
                  {loadingRiskScore ? (
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />
                      <span className="text-zinc-400 text-sm">Calculating risk score...</span>
                    </div>
                  ) : propertyRiskScore ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            propertyRiskScore.score >= 70 ? 'bg-rose-500/20' :
                            propertyRiskScore.score >= 50 ? 'bg-orange-500/20' :
                            propertyRiskScore.score >= 30 ? 'bg-amber-500/20' :
                            'bg-emerald-500/20'
                          }`}>
                            <Shield className={`w-6 h-6 ${
                              propertyRiskScore.score >= 70 ? 'text-rose-400' :
                              propertyRiskScore.score >= 50 ? 'text-orange-400' :
                              propertyRiskScore.score >= 30 ? 'text-amber-400' :
                              'text-emerald-400'
                            }`} />
                          </div>
                          <div>
                            <p className="text-xs text-zinc-500 uppercase font-medium">Compliance Risk Score</p>
                            <div className="flex items-center gap-2">
                              <span className={`text-3xl font-bold ${
                                propertyRiskScore.score >= 70 ? 'text-rose-400' :
                                propertyRiskScore.score >= 50 ? 'text-orange-400' :
                                propertyRiskScore.score >= 30 ? 'text-amber-400' :
                                'text-emerald-400'
                              }`}>
                                {propertyRiskScore.score}
                              </span>
                              <span className="text-zinc-500 text-lg">/100</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                                propertyRiskScore.level === 'critical' ? 'bg-rose-500/20 text-rose-400' :
                                propertyRiskScore.level === 'high' ? 'bg-orange-500/20 text-orange-400' :
                                propertyRiskScore.level === 'moderate' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-emerald-500/20 text-emerald-400'
                              }`}>
                                {propertyRiskScore.level}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Factors breakdown */}
                        <div className="text-right text-xs text-zinc-500 space-y-1">
                          <div>Open Violations: <span className="text-white font-medium">{propertyRiskScore.factors?.open_violations || 0}</span></div>
                          <div>Total Records: <span className="text-white font-medium">{propertyRiskScore.violation_count || 0}</span></div>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            propertyRiskScore.score >= 70 ? 'bg-gradient-to-r from-rose-500 to-rose-400' :
                            propertyRiskScore.score >= 50 ? 'bg-gradient-to-r from-orange-500 to-orange-400' :
                            propertyRiskScore.score >= 30 ? 'bg-gradient-to-r from-amber-500 to-amber-400' :
                            'bg-gradient-to-r from-emerald-500 to-emerald-400'
                          }`}
                          style={{ width: `${propertyRiskScore.score}%` }}
                        />
                      </div>
                      
                      {/* AI Analysis */}
                      {propertyRiskScore.ai_analysis && (
                        <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg border border-white/5">
                          <p className="text-xs text-zinc-500 uppercase mb-2 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse"></span>
                            AI Risk Assessment
                          </p>
                          <p className="text-sm text-zinc-300 leading-relaxed">
                            {propertyRiskScore.ai_analysis}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-zinc-500 text-sm">Risk score unavailable</div>
                  )}
                </div>

                {/* Data Source Badge */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded uppercase">
                    Live Data • {selectedProperty.plutoData.dataSource} {selectedProperty.plutoData.lastUpdated}
                  </span>
                </div>

                {/* Property Image & Quick Links */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Property Image */}
                  <div className="relative rounded-lg overflow-hidden bg-zinc-800 h-40">
                    <img 
                      src={`https://maps.googleapis.com/maps/api/streetview?size=600x300&location=${encodeURIComponent(selectedProperty?.address + ', ' + (selectedProperty?.borough || 'Manhattan') + ', New York, NY')}&fov=90&key=demo`}
                      alt={selectedProperty?.address}
                      className="w-full h-full object-cover opacity-0"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    {/* Fallback placeholder */}
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-purple-600/20 flex flex-col items-center justify-center">
                      <Building2 className="w-12 h-12 text-indigo-400/50 mb-2" />
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedProperty?.address + ', ' + (selectedProperty?.borough || 'Manhattan') + ', NY')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                      >
                        <MapPin className="w-3 h-3" />
                        View on Google Maps
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  
                  {/* Quick Links */}
                  <div className="space-y-2">
                    <h4 className="text-xs uppercase text-zinc-500 mb-2">Official Records</h4>
                    <a 
                      href={getDOBProfileUrl(selectedProperty)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-colors group"
                    >
                      <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-orange-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-white text-sm font-medium group-hover:text-orange-400 transition-colors">NYC Dept of Buildings</div>
                        <div className="text-xs text-zinc-500">View official BIS profile</div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-orange-400" />
                    </a>
                    <a 
                      href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=40.7580,-73.9855&query=${encodeURIComponent(selectedProperty?.address + ', ' + (selectedProperty?.borough || 'Manhattan'))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-colors group"
                    >
                      <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-white text-sm font-medium group-hover:text-blue-400 transition-colors">Street View</div>
                        <div className="text-xs text-zinc-500">View property exterior</div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-blue-400" />
                    </a>
                    
                    {/* Report Generation - 2-Tier System */}
                    <div className="space-y-2">
                      <p className="text-xs text-zinc-400 mb-2">Generate Compliance Report:</p>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => generateEnhancedReport(selectedProperty, 'executive')}
                          disabled={generatingReport || !selectedProperty?.address}
                          data-testid="dashboard-executive-report-btn"
                          className="p-3 rounded-lg border border-teal-500/30 bg-teal-500/10 hover:bg-teal-500/20 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-teal-400 font-medium text-sm">
                            <FileText className="w-4 h-4" />
                            Executive
                          </div>
                          <p className="text-[10px] text-zinc-500 mt-1">Summary + AI Insite</p>
                        </button>
                        <button
                          onClick={() => generateEnhancedReport(selectedProperty, 'extended')}
                          disabled={generatingReport || !selectedProperty?.address}
                          data-testid="dashboard-extended-report-btn"
                          className="p-3 rounded-lg border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-2 text-purple-400 font-medium text-sm">
                            <FileText className="w-4 h-4" />
                            Extended
                          </div>
                          <p className="text-[10px] text-zinc-500 mt-1">+ Open violations & applications</p>
                        </button>
                      </div>
                      {generatingReport && (
                        <div className="flex items-center justify-center gap-2 py-2 text-amber-400 text-xs">
                          <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                          Generating report...
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Property Identifiers */}
                <div>
                  <h4 className="text-xs uppercase text-zinc-500 mb-3 flex items-center gap-2">
                    <FileText className="w-3 h-3" /> Property Identifiers
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">BIN</div>
                      <div className="text-white font-mono">{selectedProperty.plutoData.bin || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">BBL</div>
                      <div className="text-white font-mono text-sm">{selectedProperty.plutoData.bbl ? selectedProperty.plutoData.bbl.split('.')[0] : `${selectedProperty?.block}/${selectedProperty?.lot}`}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Tax Class</div>
                      <div className="text-white">{selectedProperty.plutoData.taxClass || 'N/A'}</div>
                    </div>
                  </div>
                </div>
                
                {/* Building Info */}
                <div>
                  <h4 className="text-xs uppercase text-zinc-500 mb-3 flex items-center gap-2">
                    <Building2 className="w-3 h-3" /> Building Information
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Year Built</div>
                      <div className="text-white text-lg font-semibold">{selectedProperty.plutoData.yearBuilt || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Floors</div>
                      <div className="text-white text-lg font-semibold">{selectedProperty.plutoData.numFloors || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Residential Units</div>
                      <div className="text-white text-lg font-semibold">{selectedProperty.plutoData.residentialUnits || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Building Class</div>
                      <div className="text-white font-semibold">{selectedProperty.plutoData.buildingClass || 'N/A'}</div>
                      {selectedProperty.plutoData.buildingClassDesc && (
                        <div className="text-xs text-zinc-500 mt-0.5">{selectedProperty.plutoData.buildingClassDesc}</div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Land & Zoning */}
                <div>
                  <h4 className="text-xs uppercase text-zinc-500 mb-3 flex items-center gap-2">
                    <Layers className="w-3 h-3" /> Land & Zoning
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Lot Area (sq ft)</div>
                      <div className="text-white font-semibold">{selectedProperty.plutoData.lotArea || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Building Area (sq ft)</div>
                      <div className="text-white font-semibold">{selectedProperty.plutoData.buildingArea || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Zoning District</div>
                      <div className="text-white font-semibold">{selectedProperty.plutoData.zoningDistrict || 'N/A'}</div>
                      {selectedProperty.plutoData.zoningDistrict2 && (
                        <div className="text-xs text-zinc-500 mt-0.5">{selectedProperty.plutoData.zoningDistrict2}</div>
                      )}
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3 md:col-span-2">
                      <div className="text-xs text-zinc-500">Land Use</div>
                      <div className="text-white font-semibold text-sm">{selectedProperty.plutoData.landUse || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Community Board</div>
                      <div className="text-white font-semibold">{selectedProperty.plutoData.communityBoard || 'N/A'}</div>
                    </div>
                  </div>
                </div>
                
                {/* Property Status & Risk Indicators */}
                <div>
                  <h4 className="text-xs uppercase text-zinc-500 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3" /> Property Status & Risk Indicators
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Enhanced Flood Zone Panel */}
                    <div className={`rounded-lg p-4 border ${
                      selectedProperty.plutoData.floodZoneRisk === 'High Risk' 
                        ? 'bg-rose-500/10 border-rose-500/30' 
                        : selectedProperty.plutoData.floodZoneRisk === 'Moderate Risk'
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : selectedProperty.plutoData.floodZoneRisk === 'Elevated Risk'
                        ? 'bg-orange-500/10 border-orange-500/30'
                        : 'bg-emerald-500/10 border-emerald-500/30'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`p-1.5 rounded ${
                          selectedProperty.plutoData.floodZoneRisk === 'High Risk' 
                            ? 'bg-rose-500/20' 
                            : selectedProperty.plutoData.floodZoneRisk === 'Moderate Risk'
                            ? 'bg-amber-500/20'
                            : selectedProperty.plutoData.floodZoneRisk === 'Elevated Risk'
                            ? 'bg-orange-500/20'
                            : 'bg-emerald-500/20'
                        }`}>
                          <svg className={`w-4 h-4 ${
                            selectedProperty.plutoData.floodZoneRisk === 'High Risk' 
                              ? 'text-rose-400' 
                              : selectedProperty.plutoData.floodZoneRisk === 'Moderate Risk'
                              ? 'text-amber-400'
                              : selectedProperty.plutoData.floodZoneRisk === 'Elevated Risk'
                              ? 'text-orange-400'
                              : 'text-emerald-400'
                          }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                          </svg>
                        </div>
                        <span className="text-xs font-medium text-zinc-400">Flood Zone Status</span>
                        {selectedProperty.plutoData.floodZoneCode && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                            selectedProperty.plutoData.floodZoneCode === 'AE/VE' ? 'bg-rose-500/30 text-rose-300' :
                            selectedProperty.plutoData.floodZoneCode === 'AE' ? 'bg-orange-500/30 text-orange-300' :
                            selectedProperty.plutoData.floodZoneCode === 'X' ? 'bg-emerald-500/30 text-emerald-300' :
                            'bg-zinc-500/30 text-zinc-300'
                          }`}>
                            {selectedProperty.plutoData.floodZoneCode}
                          </span>
                        )}
                      </div>
                      <div className={`text-lg font-semibold ${
                        selectedProperty.plutoData.floodZoneRisk === 'High Risk' 
                          ? 'text-rose-400' 
                          : selectedProperty.plutoData.floodZoneRisk === 'Moderate Risk'
                          ? 'text-amber-400'
                          : selectedProperty.plutoData.floodZoneRisk === 'Elevated Risk'
                          ? 'text-orange-400'
                          : 'text-emerald-400'
                      }`}>
                        {selectedProperty.plutoData.floodZoneRisk || 'Checking...'}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 line-clamp-2" title={selectedProperty.plutoData.floodZoneDetail}>
                        {selectedProperty.plutoData.floodZoneDetail || 'FEMA FIRM Data'}
                      </div>
                      {/* Additional flood indicators */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {selectedProperty.plutoData.hurricaneEvacZone && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-300 rounded" title="NYC Hurricane Evacuation Zone">
                            🌀 {selectedProperty.plutoData.hurricaneEvacZone}
                          </span>
                        )}
                        {selectedProperty.plutoData.sandyInundation && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded" title="Flooded during Hurricane Sandy (2012)">
                            🌊 Sandy Area
                          </span>
                        )}
                        {selectedProperty.plutoData.futureFloodplain2050 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded" title="In projected 2050s floodplain">
                            📈 2050 Risk
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Landmark Status - Simple */}
                    <div className={`rounded-lg p-4 border ${
                      selectedProperty.plutoData.landmark || selectedProperty.plutoData.histDist
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-zinc-800/50 border-zinc-700/50'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`p-1.5 rounded ${
                          selectedProperty.plutoData.landmark || selectedProperty.plutoData.histDist
                            ? 'bg-amber-500/20'
                            : 'bg-zinc-700/50'
                        }`}>
                          <svg className={`w-4 h-4 ${
                            selectedProperty.plutoData.landmark || selectedProperty.plutoData.histDist
                              ? 'text-amber-400'
                              : 'text-zinc-500'
                          }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <span className="text-xs font-medium text-zinc-400">Landmark Status</span>
                      </div>
                      <div className={`text-lg font-semibold ${
                        selectedProperty.plutoData.landmark || selectedProperty.plutoData.histDist
                          ? 'text-amber-400'
                          : 'text-zinc-400'
                      }`}>
                        {selectedProperty.plutoData.landmark 
                          ? 'Yes - Landmark' 
                          : selectedProperty.plutoData.histDist 
                          ? 'Yes - Historic District'
                          : 'No'}
                      </div>
                      {(selectedProperty.plutoData.landmark || selectedProperty.plutoData.histDist) && (
                        <div className="text-xs text-zinc-400 mt-1">
                          {selectedProperty.plutoData.historicDistrictName || selectedProperty.plutoData.landmark || selectedProperty.plutoData.histDist}
                        </div>
                      )}
                    </div>
                    
                    {/* MTA Proximity */}
                    <div className={`rounded-lg p-4 border ${
                      selectedProperty.plutoData.mtaProximity === 'Adjacent' || selectedProperty.plutoData.mtaProximity === 'Very Close'
                        ? 'bg-blue-500/10 border-blue-500/30'
                        : selectedProperty.plutoData.mtaProximity
                        ? 'bg-indigo-500/10 border-indigo-500/30'
                        : 'bg-zinc-800/50 border-zinc-700/50'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`p-1.5 rounded ${
                          selectedProperty.plutoData.mtaProximity
                            ? 'bg-blue-500/20'
                            : 'bg-zinc-700/50'
                        }`}>
                          <svg className={`w-4 h-4 ${
                            selectedProperty.plutoData.mtaProximity
                              ? 'text-blue-400'
                              : 'text-zinc-500'
                          }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        </div>
                        <span className="text-xs font-medium text-zinc-400">MTA Subway Access</span>
                      </div>
                      <div className={`text-lg font-semibold ${
                        selectedProperty.plutoData.mtaProximity
                          ? 'text-blue-400'
                          : 'text-zinc-400'
                      }`}>
                        {selectedProperty.plutoData.mtaProximity || 'Checking...'}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 truncate" title={selectedProperty.plutoData.nearestSubway || 'Loading station info...'}>
                        {selectedProperty.plutoData.nearestSubway 
                          ? `${selectedProperty.plutoData.nearestSubway}${selectedProperty.plutoData.nearestSubwayDistance ? ` • ${selectedProperty.plutoData.nearestSubwayDistance}` : ''}`
                          : 'Loading station info...'}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Owner & Value */}
                <div>
                  <h4 className="text-xs uppercase text-zinc-500 mb-3 flex items-center gap-2">
                    <Users className="w-3 h-3" /> Ownership & Value
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-zinc-800/50 rounded-lg p-3 md:col-span-2">
                      <div className="text-xs text-zinc-500">Owner</div>
                      <div className="text-white font-semibold">{selectedProperty.plutoData.ownerName || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Assessed Total</div>
                      <div className="text-emerald-400 font-semibold">{selectedProperty.plutoData.assessedValue || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Market Value</div>
                      <div className="text-emerald-400 font-semibold">{selectedProperty.plutoData.marketValue || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3 md:col-span-2">
                      <div className="text-xs text-zinc-500">Assessment Period</div>
                      <div className="text-white text-sm">{selectedProperty.plutoData.fiscalYear || 'N/A'}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{selectedProperty.plutoData.assessedDate || ''}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Assessed Land</div>
                      <div className="text-white font-semibold">{selectedProperty.plutoData.assessedLand || 'N/A'}</div>
                    </div>
                    <div className="bg-zinc-800/50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">City Council</div>
                      <div className="text-white font-semibold">{selectedProperty.plutoData.council || 'N/A'}</div>
                    </div>
                  </div>
                </div>
                
                {/* Violations Summary */}
                <div>
                  <h4 className="text-xs uppercase text-zinc-500 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3" /> Violations Summary
                  </h4>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-rose-400">{selectedProperty?.open || 0}</div>
                      <div className="text-xs text-rose-400/70">Open</div>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-amber-400">{selectedProperty?.inProgress || 0}</div>
                      <div className="text-xs text-amber-400/70">In Progress</div>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-emerald-400">{selectedProperty?.closed || 0}</div>
                      <div className="text-xs text-emerald-400/70">Closed</div>
                    </div>
                    <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-indigo-400">{selectedProperty?.total || 0}</div>
                      <div className="text-xs text-indigo-400/70">Total</div>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex items-center gap-3 pt-2">
                  <Link 
                    to={`/compliance?address=${encodeURIComponent(selectedProperty?.address || '')}&block=${selectedProperty?.block}&lot=${selectedProperty?.lot}`}
                    className="btn-primary text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2"
                  >
                    <Search className="w-4 h-4" />
                    View All Violations
                  </Link>
                  <Link 
                    to={`/applications?address=${encodeURIComponent(selectedProperty?.address || '')}`}
                    className="btn-secondary text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    View Applications
                  </Link>
                </div>
              </div>
            ) : selectedProperty?.plutoError ? (
              <div className="py-8 text-center">
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-amber-400" />
                </div>
                <p className="text-amber-400 font-medium mb-2">Property Data Unavailable</p>
                <p className="text-zinc-500 text-sm mb-4">{selectedProperty.plutoError}</p>
                <p className="text-zinc-400 text-xs">
                  Block {selectedProperty?.block}, Lot {selectedProperty?.lot} may not exist in the NYC PLUTO database.
                </p>
                <div className="mt-6 flex items-center justify-center gap-3">
                  <a 
                    href={getDOBProfileUrl(selectedProperty)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary text-sm px-4 py-2 rounded-lg inline-flex items-center gap-2"
                  >
                    <Building2 className="w-4 h-4" />
                    Check DOB Profile
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-zinc-500">
                Unable to load property details
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Charts Row - Smaller */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Violations Trend */}
          <div className="glass-card p-4 rounded-xl">
            <h3 className="text-sm font-semibold text-white mb-3">Violations Trend</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="month" stroke="#71717a" fontSize={10} />
                  <YAxis stroke="#71717a" fontSize={10} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#6366f1" 
                    strokeWidth={2}
                    dot={{ fill: '#6366f1', r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Severity Distribution */}
          <div className="glass-card p-4 rounded-xl">
            <h3 className="text-sm font-semibold text-white mb-3">By Severity</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={severityData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis type="number" stroke="#71717a" fontSize={10} />
                  <YAxis dataKey="name" type="category" stroke="#71717a" fontSize={10} width={50} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Quick Actions - Compact */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link to="/compliance" className="glass-card p-4 rounded-xl hover:border-indigo-500/50 transition-colors group">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">Search Compliance</h3>
                <p className="text-xs text-zinc-400">Query the database</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-zinc-400 group-hover:text-indigo-400 transition-colors" />
            </div>
          </Link>

          <Link to="/chat" className="glass-card p-4 rounded-xl hover:border-indigo-500/50 transition-colors group">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">Help</h3>
                <p className="text-xs text-zinc-400">Ask about compliance</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-zinc-400 group-hover:text-indigo-400 transition-colors" />
            </div>
          </Link>

          <Link to="/reports" className="glass-card p-4 rounded-xl hover:border-indigo-500/50 transition-colors group">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">Property Reports</h3>
                <p className="text-xs text-zinc-400">Executive & Full PDF reports</p>
              </div>
              <ArrowUpRight className="w-4 h-4 text-zinc-400 group-hover:text-indigo-400 transition-colors" />
            </div>
          </Link>
        </div>

        {/* Datasets Access - Compact */}
        <div className="glass-card p-4 rounded-xl">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">Your Dataset Access</h3>
            {user?.tier === 'free' && (
              <Link to="/billing" className="text-xs text-indigo-400 hover:underline">Upgrade →</Link>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {user?.datasets_access?.map((ds, i) => (
              <span key={i} className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded">
                {DATASET_LABELS[ds] || ds.replace(/_/g, ' ').toUpperCase()}
              </span>
            ))}
          </div>
        </div>

        {/* Resources Section */}
        <div className="glass-card p-4 rounded-xl">
          <h3 className="text-sm font-semibold text-white mb-3">Resources</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <a 
              href="/Insite360_Executive_Summary.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg hover:bg-indigo-500/20 transition-colors group"
              data-testid="download-exec-summary"
            >
              <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Executive Summary</p>
                <p className="text-xs text-zinc-400">Platform overview & 2026 roadmap</p>
              </div>
              <Download className="w-4 h-4 text-indigo-400 group-hover:scale-110 transition-transform" />
            </a>
            
            <a 
              href="/Insite360_Presentation.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg hover:bg-purple-500/20 transition-colors group"
              data-testid="download-presentation"
            >
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Layers className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-white">Presentation Deck</p>
                <p className="text-xs text-zinc-400">Sales & investor slides</p>
              </div>
              <Download className="w-4 h-4 text-purple-400 group-hover:scale-110 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
